// Idura MitID signing — shared frontend helpers for the two contract wizards
// (erhverv + residential). Both kinds use the identical backend mechanism:
//   POST /contracts/{id}/signing-order  → creates the Idura order, emails each
//        party their personal MitID signing link, flips signing_status → 'sent'.
//   GET  /contracts/{id}/signing-order  → current order + per-signatory status.
// The backend falls back to an offline stub client when Idura creds are absent,
// so these calls still succeed (with stub hrefs) in that mode.

import { apiFetch } from './api-client'

// One signatory in the create response (role-tagged, carries the signing href).
export interface SigningOrderParty {
  role: string // 'landlord' | 'primary_tenant' | 'co_tenant'
  renter_id: string | null
  signatory_id: string
  href: string
}

export interface SigningOrderResult {
  signing_order_id: string
  signatories: SigningOrderParty[]
  is_stub?: boolean
}

// One signatory in the status response (landlord vs tenant + signed_at).
export interface SigningStatusSignatory {
  role: 'landlord' | 'tenant'
  renter_id: string | null
  signatory_id: string
  signed_at: string | null
}

export interface SigningOrderStatus {
  signing_order_id: string
  signing_status: string // created|sent|partially_signed|completed|expired|cancelled
  signed: boolean
  signatories: SigningStatusSignatory[]
}

// Create the Idura signature order for a saved contract draft. Returns the order
// + per-party signing hrefs. Throws ApiError on failure (the caller may fall
// back to the legacy stub send path).
export function createSigningOrder(contractId: string): Promise<SigningOrderResult> {
  return apiFetch<SigningOrderResult>(`/api/v1/contracts/${contractId}/signing-order`, {
    method: 'POST',
  })
}

// Poll the current signing order + per-signatory status. Returns null when no
// order exists yet (404).
export async function getSigningOrder(contractId: string): Promise<SigningOrderStatus | null> {
  try {
    return await apiFetch<SigningOrderStatus>(`/api/v1/contracts/${contractId}/signing-order`)
  } catch {
    return null
  }
}
